import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateDisplay } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  Users,
  Eye,
  Edit,
  ShieldAlert,
  EyeOff,
  AlertTriangle,
  FileText,
  CalendarDays,
} from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";
import {
  fetchGaClientView,
  gaClientPageState,
  gaClientViewUrl,
  type GaClientView as GaClientViewData,
} from "@/lib/gaClientView";

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
        <p className="text-2xl font-semibold text-foreground mt-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ScopeBadge({ scope }: { scope: GaClientViewData["scope"] }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      scope === "assist" ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300" : "bg-muted text-muted-foreground"
    }`}>
      {scope === "assist" ? <><Edit className="w-3 h-3" /> Assist</> : <><Eye className="w-3 h-3" /> View Only</>}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${
      status === "active" ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" :
      status === "pending" ? "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300" :
      "bg-muted text-muted-foreground"
    }`}>
      {status}
    </span>
  );
}

function EmptyState({ icon: Icon, title, detail }: { icon: React.ElementType; title: string; detail?: string }) {
  return (
    <div className="text-center py-24 text-muted-foreground">
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className="text-foreground font-medium">{title}</p>
      {detail && <p className="text-sm mt-1">{detail}</p>}
    </div>
  );
}

export default function GAClientView() {
  const { gaId } = useGaContext();
  const { brokerAccountId, clientId } = useParams<{ brokerAccountId: string; clientId: string }>();
  const backHref = `/ga/brokers/${brokerAccountId}`;

  const query = useQuery<GaClientViewData>({
    queryKey: ["/api/ga", gaId, "clients", clientId, { broker: brokerAccountId }],
    queryFn: () => fetchGaClientView(gaClientViewUrl(gaId, clientId!, brokerAccountId)),
    enabled: !!gaId && !!clientId,
    retry: false,
  });
  const state = gaClientPageState(query);
  const data = query.data;

  const back = (
    <Link href={backHref}>
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Broker Account
      </Button>
    </Link>
  );

  if (state === "loading") {
    return (
      <div className="space-y-6" data-testid="ga-client-loading">
        {back}
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (state !== "ready" || !data) {
    return (
      <div className="space-y-6">
        {back}
        {state === "forbidden" || state === "unauthenticated" ? (
          <EmptyState
            icon={ShieldAlert}
            title="You don't have access to this client"
            detail={state === "unauthenticated" ? "Your session has expired. Sign in again." : "Your account isn't a member of this General Agency."}
          />
        ) : state === "not_shared" ? (
          <EmptyState
            icon={EyeOff}
            title="This client isn't shared with your agency"
            detail="The broker controls which clients are shared. Ask them to share it from their Partner Access settings."
          />
        ) : (
          <EmptyState icon={AlertTriangle} title="Couldn't load this client" detail="Something went wrong. Try again in a moment." />
        )}
      </div>
    );
  }

  const { client, broker, planYear, plans } = data;
  const location = [client.city, client.state].filter(Boolean).join(", ");

  return (
    <div className="space-y-6">
      {back}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{client.name}</h1>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Building2 className="w-3 h-3" /> {broker.brokerAgencyName || "Broker"}
          </span>
          <StatusBadge status={client.status} />
          <ScopeBadge scope={data.scope} />
          <span className="text-xs text-muted-foreground">Read-only</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Employees" value={data.employeeCount} sub="Active" />
        <StatCard label="Enrolled" value={data.enrolledCount} sub={`${data.waivedCount} waived`} />
        <StatCard label="Enrollment" value={data.enrollmentPct != null ? `${data.enrollmentPct}%` : "—"} sub="of active employees" />
        <StatCard
          label="Renewal"
          value={planYear.renewalDate ? formatDateDisplay(planYear.renewalDate) : "—"}
          sub={planYear.effectiveDate ? `Plan year from ${formatDateDisplay(planYear.effectiveDate)}` : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> Client Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow label="Location" value={location} />
            <InfoRow label="Primary Contact" value={client.primaryContactName} />
            <InfoRow label="Contact Email" value={client.contactEmail} />
            <InfoRow label="Contact Phone" value={client.contactPhone} />
            <InfoRow label="Group Number" value={client.groupNumber} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> Plan Year &amp; Sharing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow label="Plan Year Effective" value={planYear.effectiveDate ? formatDateDisplay(planYear.effectiveDate) : null} />
            <InfoRow label="Renewal Date" value={planYear.renewalDate ? formatDateDisplay(planYear.renewalDate) : null} />
            <InfoRow label="Renewal Stage" value={client.renewalStage?.replace(/_/g, " ")} />
            <InfoRow label="Broker Agency" value={broker.brokerAgencyName} />
            <InfoRow label="Shared Since" value={data.sharedAt ? new Date(data.sharedAt).toLocaleDateString() : null} />
          </CardContent>
        </Card>
      </div>

      {/* Plans */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <FileText className="w-4 h-4" /> Lines of Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No active plans on file</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left pb-2">Line</th>
                  <th className="text-left pb-2">Plan</th>
                  <th className="text-left pb-2">Carrier</th>
                  <th className="text-left pb-2">Effective</th>
                  <th className="text-left pb-2">Renewal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {plans.map((p) => (
                  <tr key={p.id}>
                    <td className="py-2.5 capitalize">{p.planType}</td>
                    <td className="py-2.5 font-medium">{p.planName}</td>
                    <td className="py-2.5 text-muted-foreground">{p.carrierName || "—"}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">{formatDateDisplay(p.effectiveDate) || "—"}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">{formatDateDisplay(p.renewalDate || p.endDate) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
